import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ListingResource,
  MediaCategory,
  MediaResource,
  useListingField,
  useMediaResource,
  useSyncedMediaResource,
} from '@brightmls/nextgen.lae.flc';
import { useDropzone } from 'react-dropzone';
import { Box, Typography } from '@mui/material';
import { PropertyField } from 'src/system/field';
import { useMediaUpload } from '../../media/hooks/useMediaUpload';
import { useService } from 'src/contexts/ServiceContext';
import { useMediaValidation } from '../../media/hooks/useMediaValidation';
import { MediaType } from 'src/system/media';
import { useModal } from '../../../../contexts/ModalContext';
import { OfficeExclusiveType } from './types';

/**
 * Hook for single file upload
 */
export const useFileUpload = (options: any) => {
  const [preUploadedFile, setPreUploadedFile] = useState<any>([]);
  const pendingUploadsRef = useRef(new Set<number>());
  const { showModal } = useModal();
  // const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const { deleteListingResources } = useService();

  const listingKeyField = useListingField(
    ListingResource.Property,
    PropertyField.LISTING_KEY,
    '',
  );
  const propertyTypeField = useListingField(
    ListingResource.Property,
    PropertyField.PROPERTY_TYPE,
    '',
  );
  const editKeyField = useListingField(
    ListingResource.Property,
    PropertyField.EDIT_KEY,
    '',
  );
  const countyField = useListingField(
    ListingResource.Property,
    PropertyField.COUNTY,
    '',
  );

  const listingKey = listingKeyField.value;

  const { media: allMedia } = useMediaResource();

  const { validateUploadSet } = useMediaValidation();

  const {
    items: localFiles,
    setMediaCallback: setLocalFiles,
    displayItems: displayFiles,
  } = useSyncedMediaResource([MediaCategory.Document, MediaCategory.FloorPlan]);

  const isOfficeExclusiveDocument = useCallback((file: MediaResource) => {
    return file.DOCUMENT_TYPE === OfficeExclusiveType.OFFICE_EXCLUSIVE;
  }, []);

  const uploadedFiles = useMemo(() => {
    if (localFiles.length === 0) return [];
    return localFiles.filter(isOfficeExclusiveDocument);
  }, [localFiles]);

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
    },
    disabled: false,
    maxFiles: 1,
    minSize: 0,
    multiple: false,
    noClick: uploadedFiles.length > 0,
    noDrag: uploadedFiles.length > 0,
    onDrop: async (acceptedFiles: File[]) => {
      await options?.handleDrop?.(acceptedFiles);
    },
  });

  const handleUploadComplete = useCallback(
    (result: any) => {
      const newMediaFile = result;
      pendingUploadsRef.current.delete(newMediaFile.MEDIA_ITEM_NUMBER);

      setLocalFiles((prevFiles) => {
        const newFiles = [...prevFiles];

        // Assign display order that ensures placement at the end
        newFiles.push(newMediaFile);

        // Only update the resource if this is the last pending upload
        if (pendingUploadsRef.current.size === 0) {
          // Files are automatically synced by useSyncedMediaResource
        }

        return newFiles;
      });

      // options?.onSuccessUpload?.();
    },
    [pendingUploadsRef.current, setLocalFiles],
  );

  const { uploadMediaBatch, isUploading, uploadProgress } = useMediaUpload({
    listingKey,
    listingDetails: {
      CWAPropertyType: propertyTypeField.value,
      CWALISKey: editKeyField.value,
      CWACounty: countyField.value,
    },
    onUploadComplete: handleUploadComplete,
    onUploadError: (error, metadata) => {
      console.error(`Failed to upload ${metadata.name}:`, error);
    },
    onBatchComplete: () => {
      options?.onSuccessUpload?.(localFiles);
    },
  });

  const onDeleteDocument = useCallback(
    async (fileVWKey: string) => {
      // First update local state
      setLocalFiles((prevFiles) => {
        const updatedFiles = prevFiles
          .filter((file) => file.MED_VW_KEY !== fileVWKey)
          .map((file, index) => ({
            ...file,
            MEDIA_DISPLAY_ORDER: index,
          }));

        return updatedFiles;
      });

      // Delete from the backend
      deleteListingResources(parseInt(listingKey || ''), [
        {
          type: 'Media',
          itemKey: parseInt(fileVWKey),
        },
      ]).catch((error) => {
        console.error('Failed to delete document:', error);
        // You may want to add error handling UI feedback here
      });

      return true;
    },
    // [deleteListingResources, listingKey, setLocalFiles, setIsDeleting],
    [deleteListingResources, listingKey, setLocalFiles],
  );

  const handleUpload = useCallback(async () => {
    if (!preUploadedFile.length) {
      return;
    }

    try {
      const currentDocuments = allMedia.filter(
        (m: any) =>
          m.MEDIA_CATEGORY === 'Document' || m.MEDIA_CATEGORY === 'Floor Plan',
      );

      const validFiles = await validateUploadSet({
        files: preUploadedFile.map((puf: any) => puf.file),
        type: MediaType.Document,
        currentCount: currentDocuments.length,
        docType: OfficeExclusiveType.OFFICE_EXCLUSIVE,
      });

      if (validFiles.length === 0) {
        return;
      }

      // Find the true maximum item number including any pending uploads
      const maxItemNumber = Math.max(
        ...allMedia.map((item: any) => item.MEDIA_ITEM_NUMBER),
        ...localFiles.map((item: any) => item.MEDIA_ITEM_NUMBER),
        0,
      );

      const baseDisplayOrder =
        Math.max(...localFiles.map((f) => f.MEDIA_DISPLAY_ORDER), -1) + 1;

      // Map the valid files back to their floor plan settings
      const mediaItems = validFiles.map((file, index) => {
        const itemNumber = maxItemNumber + index + 1;

        // Track this upload
        pendingUploadsRef.current.add(itemNumber);

        return {
          name: file.name,
          type: file.type,
          category: 'Document',
          visibility: 'AgentOnly',
          description: '',
          shortDescription:
            file.name.substring(0, file.name.lastIndexOf('.')) || file.name,
          size: file.size,
          file,
          itemNumber: itemNumber,
          displayOrder: baseDisplayOrder + index,
          docType: OfficeExclusiveType.OFFICE_EXCLUSIVE,
        };
      });

      await uploadMediaBatch(mediaItems);
      setPreUploadedFile([]);
    } catch (error) {
      console.error('Upload validation failed:', error);
    }
  }, [preUploadedFile]);

  const confirmModal = useCallback((file: MediaResource | null = null) => {
    showModal({
      title: 'Replace Office Exclusive Document', // Optional title
      content: (
        <Box>
          <Typography variant="body1">
            This listing requires an Office Exclusive file.
            <br />
            <br />
            If you delete the current file, you must upload a new one right away
            to keep the listing published.
          </Typography>
        </Box>
      ),
      width: 'xs',
      confirmText: 'Delete and Replace',
      cancelText: 'Cancel',
      onConfirm: async () => {
        options?.onConfirm?.(file);
      },
      onCancel: () => {
        options?.onCancel?.();
      },
    });
  }, []);

  useEffect(() => {
    if (preUploadedFile.length) {
      handleUpload();
    }
  }, [preUploadedFile]);

  return {
    isUploading,
    onDeleteDocument,
    setPreUploadedFile,
    uploadedFiles,
    uploadProgress,
    getRootProps,
    getInputProps,
    open,
    confirmModal,
    isOfficeExclusiveDocument,
  };
};
